import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Search, Clock, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Industry Insights': 'bg-blue-100 text-blue-800',
  'Technical Guides': 'bg-emerald-100 text-emerald-800',
  'Coatings & Linings': 'bg-purple-100 text-purple-800',
  'Project Management': 'bg-amber-100 text-amber-800',
  'Safety & Compliance': 'bg-red-100 text-red-800',
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const BlogList = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory) params.set('category', activeCategory);
        const res = await fetch(`${API}/api/blog/posts?${params}`);
        const data = await res.json();
        setPosts(data.posts || []);
        setCategories(data.categories || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [activeCategory]);

  const filtered = search
    ? posts.filter(p => `${p.title} ${p.excerpt} ${(p.tags||[]).join(' ')}`.toLowerCase().includes(search.toLowerCase()))
    : posts;

  // Split first post as featured
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Research & Learn | BCS Industrial Contractor" description="Expert insights on industrial coatings, pipeline repair, structural rehabilitation, and asset protection from BCS — AMPP/NACE certified contractors." path="/research-learn" />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-blue-300 text-sm font-semibold uppercase tracking-wider mb-3">Research & Learn</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Industry Insights & Technical Guides</h1>
          <p className="text-base text-blue-100/80 max-w-2xl mx-auto">Expert knowledge from AMPP/NACE certified professionals on coatings, rehabilitation, and asset protection.</p>
        </div>
      </section>

      {/* Search + Filters */}
      <section className="py-6 px-4 border-b bg-gray-50/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md w-full">
              <Search className="absolute left-3 top-3 text-gray-400" size={16} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm bg-white" data-testid="blog-search" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setActiveCategory('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!activeCategory ? 'bg-slate-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border'}`}>
                All
              </button>
              {categories.map(c => (
                <button key={c} onClick={() => setActiveCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeCategory === c ? 'bg-slate-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="text-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <p className="text-lg">No articles found</p>
            </div>
          ) : (
            <>
              {/* Featured Post (first) */}
              {featured && (
                <div className="mb-10 cursor-pointer group" onClick={() => navigate(`/research-learn/${featured.slug}`)} data-testid={`blog-card-${featured.slug}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300">
                    {featured.featured_image ? (
                      <div className="h-64 md:h-auto overflow-hidden">
                        <img src={featured.featured_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    ) : (
                      <div className="h-64 md:h-auto bg-gradient-to-br from-slate-800 to-blue-900 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white/20">BCS</span>
                      </div>
                    )}
                    <div className="p-6 md:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={`text-[10px] ${CATEGORY_COLORS[featured.category] || 'bg-gray-100'}`}>{featured.category}</Badge>
                        <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={10} />{formatDate(featured.published_at)}</span>
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-800 transition-colors leading-tight">{featured.title}</h2>
                      <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-3">{featured.excerpt}</p>
                      <span className="text-sm text-blue-700 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read Article <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Rest of posts in grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map(post => (
                  <Card key={post.id} className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-gray-200 hover:border-blue-300 overflow-hidden"
                    onClick={() => navigate(`/research-learn/${post.slug}`)} data-testid={`blog-card-${post.slug}`}>
                    {/* Image */}
                    {post.featured_image ? (
                      <div className="h-44 overflow-hidden">
                        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    ) : (
                      <div className="h-2 bg-gradient-to-r from-blue-900 to-blue-600" />
                    )}
                    <CardContent className="pt-4 pb-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`text-[10px] ${CATEGORY_COLORS[post.category] || 'bg-gray-100'}`}>{post.category}</Badge>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1"><Clock size={10} />{formatDate(post.published_at)}</span>
                      </div>
                      <h2 className="text-base font-bold text-slate-900 mb-2 group-hover:text-blue-800 transition-colors line-clamp-2 leading-snug">{post.title}</h2>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3 leading-relaxed">{post.excerpt}</p>
                      <span className="text-xs text-blue-700 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight size={12} />
                      </span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogList;
